import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PageKey } from '../types';

type NavItem = {
  key: PageKey;
  labelKey: string;
  icon: ReactNode;
  badge?: number;
};

type NavGroup = { labelKey: string; items: NavItem[] };

const NAV: NavGroup[] = [
  {
    labelKey: 'groups.main',
    items: [
      {
        key: 'dashboard',
        labelKey: 'items.dashboard',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        ),
      },
      {
        key: 'leads',
        labelKey: 'items.leads',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87" />
            <path d="M16 3.13a4 4 0 010 7.75" />
          </svg>
        ),
      },
      {
        key: 'clients',
        labelKey: 'items.clients',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <circle cx="9" cy="7" r="4" />
            <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
          </svg>
        ),
      },
      {
        key: 'contacts',
        labelKey: 'items.contacts',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <path d="M4 4h16v16H4z" />
            <circle cx="12" cy="10" r="3" />
            <path d="M7 18a5 5 0 0110 0" />
          </svg>
        ),
      },
      {
        key: 'cases',
        labelKey: 'items.cases',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" />
            <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
          </svg>
        ),
      },
      {
        key: 'documents',
        labelKey: 'items.documents',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        ),
      },
      {
        key: 'calendar',
        labelKey: 'items.calendar',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        ),
      },
      {
        key: 'tasks',
        labelKey: 'items.tasks',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
        ),
      },
      {
        key: 'time',
        labelKey: 'items.time',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        ),
      },
      {
        key: 'billing',
        labelKey: 'items.billing',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" />
          </svg>
        ),
      },
      {
        key: 'settings',
        labelKey: 'items.settings',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        ),
      },
    ],
  },
  {
    labelKey: 'groups.actions',
    items: [
      {
        key: 'addclient',
        labelKey: 'items.addclient',
        icon: (
          <svg className="nav-icon" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <circle cx="12" cy="8" r="4" />
            <path d="M6 20v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
            <line x1="19" y1="8" x2="23" y2="8" />
            <line x1="21" y1="6" x2="21" y2="10" />
          </svg>
        ),
      },
    ],
  },
];

export function Sidebar({
  currentPage,
  onNavigate,
  firmName,
  userName,
  userRole,
  onSignOut,
  mobileOpen = false,
  onCloseMobile,
  collapsed = false,
  onToggleCollapse,
}: {
  currentPage: PageKey;
  onNavigate: (page: PageKey) => void;
  firmName: string;
  userName: string;
  userRole: string;
  onSignOut: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { t } = useTranslation('nav');
  function handleNav(p: PageKey) {
    onNavigate(p);
    onCloseMobile?.();
  }
  return (
    <>
      {/* Backdrop — only rendered when the mobile drawer is open */}
      {mobileOpen && onCloseMobile && (
        <div
          className="sidebar-backdrop"
          onClick={onCloseMobile}
          aria-hidden
        />
      )}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="brand" title={collapsed ? firmName : undefined}>
          {collapsed ? firmName.slice(0, 2) : firmName}
        </div>
        {!collapsed && <div className="brand-sub">{t('sidebar.subtitle')}</div>}
        {/* Collapse toggle — desktop only (CSS hides on mobile) */}
        {onToggleCollapse && (
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            <svg
              className={`sidebar-caret ${collapsed ? 'collapsed' : ''}`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
      </div>

      <nav className="nav">
        {NAV.map((group) => (
          <div key={group.labelKey}>
            {!collapsed && <div className="nav-group-label">{t(group.labelKey)}</div>}
            {group.items.map((item) => (
              <div
                key={item.key}
                className={`nav-item ${currentPage === item.key ? 'active' : ''}`}
                onClick={() => handleNav(item.key)}
                title={collapsed ? t(item.labelKey) : undefined}
              >
                {item.icon}
                <span className="nav-item-label">{t(item.labelKey)}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className="nav-badge">{item.badge}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="user-pill" title={collapsed ? `${userName} · ${userRole}` : undefined}>
          <div className="user-av">{userName.slice(0, 2)}</div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="user-name">{userName}</div>
              <div className="user-role">{userRole}</div>
            </div>
          )}
          <button
            onClick={onSignOut}
            className="signout-btn"
            title={t('sidebar.sign_out')}
            aria-label={t('sidebar.sign_out')}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
